import { type FC, useState } from "react";
import { Loader2, Plus } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { AccountRow } from "@/pages/postloss/modules/components/AccountRow";
import { buildItemSubtitle } from "@/pages/postloss/modules/components/itemSubtitles";
import { CategoryEditDialog } from "@/shared/components/EstateItems/CategoryEditDialog";
import { SUPPORTED_CATEGORIES } from "@/shared/components/EstateItems/categoryEditHelpers";
import { EditEstateItemModal } from "@/shared/components/EstateItems/EditEstateItemModal";
import { AcceptDeclineActions } from "@/shared/components/EstateItems/AcceptDeclineActions";
import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { EstateItem } from "@/shared/types/estateTypes";

import searchAccountsIllustration from "@/shared/assets/illustrations/search_accounts.svg";

export const ReviewAccounts: FC<FlowCustomComponentProps> = ({ goToScreen }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const {
    assets,
    isLoading,
    updateItem,
    isUpdatingItem,
    deleteItem,
    isDeletingItem,
    itemActions: { handleAccept, handleDecline, getItemLoadingAction },
  } = useEstateItemActions(estateId);

  const bankAccounts = filterByCategory(assets, "Bank accounts");
  const investmentAccounts = assets.filter((item) =>
    [
      "Brokerage accounts",
      "Tax-advantaged savings",
      "Retirement accounts",
      "Crypto accounts",
      "NFTs",
      "Other digital assets",
    ].includes(item.category),
  );

  const hasAccounts = bankAccounts.length > 0 || investmentAccounts.length > 0;

  const [editingItem, setEditingItem] = useState<EstateItem | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-secondary" />
      </div>
    );
  }

  const renderActions = (item: EstateItem) => {
    if (item.extraction_status === "pending") {
      return (
        <AcceptDeclineActions
          onAccept={() => handleAccept(item.id)}
          onDecline={() => handleDecline(item.id)}
          loadingAction={getItemLoadingAction(item.id)}
        />
      );
    }
    return undefined;
  };

  return (
    <div className="rounded-lg border border-primary bg-white p-6">
      <div className="flex flex-col gap-6">
        {!hasAccounts && (
          <div className="flex flex-col items-center py-6">
            <img
              src={searchAccountsIllustration}
              alt="No accounts added yet"
              className="h-[100px] w-[100px]"
            />
            <p className="py-2 text-base font-semibold">No accounts added yet</p>
            <Button variant="outline" onClick={() => goToScreen("connect_accounts")}>
              <Plus className="h-4 w-4" />
              Add account
            </Button>
          </div>
        )}

        {bankAccounts.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold">Bank accounts</p>
              <button
                type="button"
                onClick={() => goToScreen("connect_accounts")}
                className="flex items-center gap-1 text-sm text-secondary hover:text-primary"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>
            {bankAccounts.map((item) => (
              <AccountRow
                key={item.id}
                item={item}
                subtitle={buildItemSubtitle(item)}
                onEdit={setEditingItem}
                actions={renderActions(item)}
              />
            ))}
          </div>
        )}

        {investmentAccounts.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold">Investment & retirement accounts</p>
              <button
                type="button"
                onClick={() => goToScreen("connect_accounts")}
                className="flex items-center gap-1 text-sm text-secondary hover:text-primary"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>
            {investmentAccounts.map((item) => (
              <AccountRow
                key={item.id}
                item={item}
                subtitle={buildItemSubtitle(item)}
                onEdit={setEditingItem}
                actions={renderActions(item)}
              />
            ))}
          </div>
        )}
      </div>
      {editingItem && SUPPORTED_CATEGORIES.has(editingItem.category) ? (
        <CategoryEditDialog
          key={editingItem.id}
          item={editingItem}
          isOpen
          onClose={() => setEditingItem(null)}
          updateItem={updateItem}
          isUpdatingItem={isUpdatingItem}
          deleteItem={deleteItem}
          isDeletingItem={isDeletingItem}
        />
      ) : editingItem ? (
        <EditEstateItemModal
          key={editingItem.id}
          isOpen
          onClose={() => setEditingItem(null)}
          estateId={editingItem.estate_id}
          item={editingItem}
        />
      ) : null}
    </div>
  );
};
